import React, { useEffect, useRef, useState } from 'react';
import type { OfficialAssetServerMaterial } from '$/api/server.types';
import { useLanguageStore } from '$/store/languageStore';
import MaterialCard from '../MaterialCard';
import MaterialEmptyState from '../MaterialEmptyState';

import styles from './index.module.scss';
import type { MaterialEmptyStateType } from '../MaterialEmptyState';

const MATERIAL_CARD_MIN_WIDTH = 180;
const MATERIAL_CARD_MAX_WIDTH = 240;
const MATERIAL_CARD_GAP = 8;
const MATERIAL_GRID_FALLBACK_COLUMNS = 4;

export interface MaterialListProps {
  materials: OfficialAssetServerMaterial[];
  loading: boolean;
  loadingMore: boolean;
  emptyStateType: MaterialEmptyStateType;
  selectedMaterialId?: number;
  scrollToMaterialId?: string | number;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  onMaterialClick: (material: OfficialAssetServerMaterial) => void;
  batchMode?: boolean;
  batchSelectedMaterialIds?: Set<number>;
  addLabel?: string;
  onMaterialAdd?: (material: OfficialAssetServerMaterial) => void;
  isMaterialAddDisabled?: (material: OfficialAssetServerMaterial) => boolean;
}

/**
 * 根据容器宽度计算官方素材列表列数，尽量让单个卡片宽度保持在 180px 到 240px。
 */
function calculateMaterialGridColumns(containerWidth: number): number {
  if (containerWidth <= 0) {
    return MATERIAL_GRID_FALLBACK_COLUMNS;
  }

  /** 按当前列数反推单张素材卡片的实际宽度。 */
  const getCardWidth = (columnCount: number): number => (
    (containerWidth - MATERIAL_CARD_GAP * (columnCount - 1)) / columnCount
  );
  let columns = Math.max(1, Math.round((containerWidth + MATERIAL_CARD_GAP) / (MATERIAL_CARD_MIN_WIDTH + MATERIAL_CARD_GAP)));

  while (columns > 1 && getCardWidth(columns) < MATERIAL_CARD_MIN_WIDTH) {
    columns -= 1;
  }
  while (
    getCardWidth(columns) > MATERIAL_CARD_MAX_WIDTH
    && getCardWidth(columns + 1) >= MATERIAL_CARD_MIN_WIDTH
  ) {
    columns += 1;
  }

  return columns;
}

/**
 * 官方资产素材列表，按真实素材数据渲染卡片；滚动加载由父级滚动容器托管。
 */
function MaterialList({
  materials,
  loading,
  loadingMore,
  emptyStateType,
  selectedMaterialId,
  scrollToMaterialId,
  scrollContainerRef,
  onMaterialClick,
  batchMode = false,
  batchSelectedMaterialIds = new Set(),
  addLabel,
  onMaterialAdd,
  isMaterialAddDisabled,
}: MaterialListProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollTargetCardRef = useRef<HTMLButtonElement>(null);
  const scrolledMaterialIdRef = useRef('');
  const [columns, setColumns] = useState(MATERIAL_GRID_FALLBACK_COLUMNS);
  const isEmpty = materials.length === 0;
  let listContent: React.ReactNode;

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    /** 根据列表容器宽度同步素材卡片列数。 */
    const updateColumns = (): void => {
      const nextColumns = calculateMaterialGridColumns(listElement.clientWidth);
      setColumns((currentColumns) => (currentColumns === nextColumns ? currentColumns : nextColumns));
    };

    updateColumns();
    const resizeObserver = new ResizeObserver(updateColumns);
    resizeObserver.observe(listElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /** 打开前已有的素材渲染完成后滚动定位，弹框内重新选择不会触发滚动。 */
  useEffect(() => {
    const scrollTargetId = scrollToMaterialId === undefined ? '' : String(scrollToMaterialId);
    if (!scrollTargetId) {
      scrolledMaterialIdRef.current = '';
      return undefined;
    }
    if (
      scrolledMaterialIdRef.current === scrollTargetId
      || !scrollTargetCardRef.current
    ) {
      return undefined;
    }
    const frameId = window.requestAnimationFrame(() => {
      const targetElement = scrollTargetCardRef.current;
      const scrollContainer = scrollContainerRef?.current;
      if (!targetElement) {
        return;
      }
      if (scrollContainer) {
        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetTop = scrollContainer.scrollTop + targetRect.top - containerRect.top;
        scrollContainer.scrollTop = Math.max(
          0,
          targetTop - (scrollContainer.clientHeight - targetRect.height) / 2,
        );
      } else {
        targetElement.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
      scrolledMaterialIdRef.current = scrollTargetId;
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [materials, scrollContainerRef, scrollToMaterialId]);

  if (isEmpty && loading) {
    listContent = (
      <div className={styles.materialLoadingState}>
        {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading1)}
      </div>
    );
  } else if (isEmpty) {
    listContent = <MaterialEmptyState type={emptyStateType} />;
  } else {
    listContent = (
      <div
        className={styles.materialGrid}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {materials.map((material) => {
          const selected = material.id === selectedMaterialId;
          return (
            <MaterialCard
              key={material.id}
              cardRef={String(material.id) === String(scrollToMaterialId) ? scrollTargetCardRef : undefined}
              cover={material.main_image.url}
              title={material.name}
              tags={material.tags}
              coverWidth={material.main_image.width}
              coverHeight={material.main_image.height}
              selected={selected}
              onClick={() => onMaterialClick(material)}
              batchMode={batchMode}
              batchSelected={batchSelectedMaterialIds.has(material.id)}
              addLabel={addLabel}
              onAdd={onMaterialAdd ? () => onMaterialAdd(material) : undefined}
              addDisabled={isMaterialAddDisabled?.(material)}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div ref={listRef} className={styles.materialList}>
      {listContent}
      {!isEmpty && loadingMore && (
        <div className={styles.materialLoadingMore}>
          {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading1)}
        </div>
      )}
    </div>
  );
}

export default MaterialList;
